import { DragDropContext, Droppable, DropResult } from "@hello-pangea/dnd";
import Button from "@shared/components/ui/Button";
import { PlusIcon } from "@shared/components/ui/Icon";
import theme from "@shared/utils/themeColors";
import React, { useState } from "react";
import toast from "react-hot-toast";
import ModalBlank from "../../../ui/modals/ModalBlank";
import { GroupedTasks, TaskStatus } from "../types/task.types";
import { TaskForm } from "./TaskForm";
import { TaskItem } from "./TaskItem";

interface TaskListProps {
  tasks: GroupedTasks;
  loading: boolean;
  onCreateTask: (data: {
    title: string;
    description?: string;
    dueDate?: Date;
  }) => Promise<void>;
  onUpdateTask: (
    taskId: string,
    data: { title: string; description?: string; dueDate?: Date }
  ) => Promise<void>;
  onDeleteTask: (taskId: string) => Promise<void>;
  onToggleStatus: (taskId: string, currentStatus: TaskStatus) => Promise<void>;
  onAddSubtask: (taskId: string, title: string) => Promise<void>;
  onToggleSubtask: (taskId: string, subtaskId: string) => Promise<void>;
  onUpdateSubtask: (
    taskId: string,
    subtaskId: string,
    title: string
  ) => Promise<void>;
  onDeleteSubtask: (taskId: string, subtaskId: string) => Promise<void>;
  onReorderSubtask: (taskId: string, subtaskIds: string[]) => Promise<void>;
  setTasks: (tasks: GroupedTasks) => void;
  updatePositions: (status: TaskStatus, taskIds: string[]) => Promise<void>;
}

export const TaskList: React.FC<TaskListProps> = ({
  tasks,
  loading,
  onCreateTask,
  onUpdateTask,
  onDeleteTask,
  onToggleStatus,
  onAddSubtask,
  onToggleSubtask,
  onUpdateSubtask,
  onDeleteSubtask,
  onReorderSubtask,
  setTasks,
  updatePositions,
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false);

  const groupedTasks = {
    pending: tasks.pending,
    completed: tasks.completed,
  };

  const handleDragEnd = async (result: DropResult): Promise<void> => {
    if (!result.destination) return;

    const { source, destination } = result;
    const sourceStatus = source.droppableId as TaskStatus;
    const destinationStatus = destination.droppableId as TaskStatus;

    // Create a copy of the tasks
    const updatedTasks = { ...tasks };
    const sourceList = Array.from(updatedTasks[sourceStatus]);
    const destinationList =
      sourceStatus === destinationStatus
        ? sourceList
        : Array.from(updatedTasks[destinationStatus]);

    // Store original state in case we need to rollback
    const originalTasks = { ...tasks };

    // Remove from source
    const [movedTask] = sourceList.splice(source.index, 1);

    // Update task status if moving between lists
    if (sourceStatus !== destinationStatus) {
      movedTask.status = destinationStatus;
    }

    // Add to destination
    destinationList.splice(destination.index, 0, movedTask);

    // Update the state immediately for optimistic UI
    if (sourceStatus === destinationStatus) {
      updatedTasks[sourceStatus] = sourceList;
    } else {
      updatedTasks[sourceStatus] = sourceList;
      updatedTasks[destinationStatus] = destinationList;
    }

    // Update UI immediately
    setTasks(updatedTasks);

    try {
      // Perform backend updates
      if (sourceStatus === destinationStatus) {
        await updatePositions(
          sourceStatus,
          sourceList.map((task) => task.id)
        );
      } else {
        await Promise.all([
          updatePositions(
            sourceStatus,
            sourceList.map((task) => task.id)
          ),
          updatePositions(
            destinationStatus,
            destinationList.map((task) => task.id)
          ),
        ]);
      }
    } catch (error) {
      // Revert to original state if there's an error
      setTasks(originalTasks);
      // Show error message to user
      console.error("Failed to update task position. Please try again.", error);
      toast.error("Failed to update task position. Please try again.");
      // You might want to add a toast notification here
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            {/* Header */}
            <div className="sm:flex sm:justify-between sm:items-center mb-8">
              {/* Left: Title */}
              <div className="mb-4 sm:mb-0">
                <h1
                  className={`text-2xl md:text-3xl ${theme.text.primary} font-bold`}
                >
                  Task Management
                </h1>
              </div>

              {/* Right: Actions */}
              <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-4">
                <Button
                  onClick={() => setShowCreateModal(true)}
                  leftIcon={<PlusIcon />}
                >
                  Add Task
                </Button>
              </div>
            </div>

            {/* Task Lists */}
            <div className="space-y-6">
              {/* To Do Group */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className={`font-semibold ${theme.text.primary}`}>
                    To Do
                  </h2>
                  <span className={`text-sm ${theme.text.muted}`}>
                    {groupedTasks.pending.length}{" "}
                    {groupedTasks.pending.length === 1 ? "task" : "tasks"}
                  </span>
                </div>
                <Droppable droppableId="pending">
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="space-y-2 min-h-[50px] bg-gray-50 dark:bg-gray-900 p-4 rounded-lg transition-all duration-200 border-2 border-dashed border-transparent hover:border-gray-200 dark:hover:border-gray-700"
                    >
                      {groupedTasks.pending.map((task, index) => (
                        <TaskItem
                          key={task.id}
                          task={task}
                          index={index}
                          onToggleStatus={onToggleStatus}
                          onUpdateTask={onUpdateTask}
                          onDeleteTask={onDeleteTask}
                          onAddSubtask={onAddSubtask}
                          onToggleSubtask={onToggleSubtask}
                          onUpdateSubtask={onUpdateSubtask}
                          onDeleteSubtask={onDeleteSubtask}
                          onReorderSubtask={onReorderSubtask}
                          isCompleted={false}
                        />
                      ))}
                      {provided.placeholder}
                      {groupedTasks.pending.length === 0 && (
                        <div className={`text-center py-4 ${theme.text.muted}`}>
                          No tasks
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              </div>

              {/* Completed Group */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className={`font-semibold ${theme.text.primary}`}>
                    Completed
                  </h2>
                  <span className={`text-sm ${theme.text.muted}`}>
                    {groupedTasks.completed.length}{" "}
                    {groupedTasks.completed.length === 1 ? "task" : "tasks"}
                  </span>
                </div>
                <Droppable droppableId="completed">
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="space-y-2 min-h-[50px] bg-gray-50 dark:bg-gray-900 p-4 rounded-lg transition-all duration-200 border-2 border-dashed border-transparent hover:border-gray-200 dark:hover:border-gray-700"
                    >
                      {groupedTasks.completed.map((task, index) => (
                        <TaskItem
                          key={task.id}
                          task={task}
                          index={index}
                          onToggleStatus={onToggleStatus}
                          onUpdateTask={onUpdateTask}
                          onDeleteTask={onDeleteTask}
                          onAddSubtask={onAddSubtask}
                          onToggleSubtask={onToggleSubtask}
                          onUpdateSubtask={onUpdateSubtask}
                          onDeleteSubtask={onDeleteSubtask}
                          onReorderSubtask={onReorderSubtask}
                          isCompleted={true}
                        />
                      ))}
                      {provided.placeholder}
                      {groupedTasks.completed.length === 0 && (
                        <div className={`text-center py-4 ${theme.text.muted}`}>
                          No completed tasks
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Task Modal */}
      <ModalBlank
        id="create-task-modal"
        modalOpen={showCreateModal}
        setModalOpen={setShowCreateModal}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">
              Create Task
            </h2>
            <button
              className="text-slate-400 hover:text-slate-500"
              onClick={() => setShowCreateModal(false)}
            >
              <span className="sr-only">Close</span>
              <svg className="w-4 h-4 fill-current">
                <path d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.414L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z" />
              </svg>
            </button>
          </div>
          <TaskForm
            onSubmit={async (data) => {
              await onCreateTask(data);
              setShowCreateModal(false);
            }}
            onCancel={() => setShowCreateModal(false)}
          />
        </div>
      </ModalBlank>
    </DragDropContext>
  );
};
